import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { useOktaAuth } from '@okta/okta-react';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: { name: string; email?: string; sub: string };
  login: () => void;
  logout: () => Promise<void>;
  getAccessToken: () => string | undefined;
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { authState, oktaAuth } = useOktaAuth();

  const isAuthenticated = authState?.isAuthenticated || false;
  const isLoading: boolean = !authState || !!authState.isPending;

  const user = {
    name: authState?.idToken?.claims?.name || 
          authState?.idToken?.claims?.preferred_username || 
          authState?.idToken?.claims?.email || 'User',
    email: authState?.idToken?.claims?.email,
    sub: authState?.idToken?.claims?.sub || '',
  };

  const login = () => { oktaAuth.signInWithRedirect(); };
  const logout = async () => { await oktaAuth.signOut(); };
  const getAccessToken = () => oktaAuth.getAccessToken();
  
  const fetchWithAuth = async (url: string, options: RequestInit = {}, retryCount = 0): Promise<Response> => {
    const maxRetries = 1;
    
    try {
      const accessToken = oktaAuth.getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      const headers = {
        ...options.headers,
        Authorization: `Bearer ${accessToken}`,
      };

      const response = await fetch(url, { ...options, headers });
      
      // Handle 401 with automatic retry after token renewal
      if (response.status === 401 && retryCount < maxRetries) {
        // Wait a bit for automatic token renewal
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchWithAuth(url, options, retryCount + 1);
      }
      
      return response;
    } catch (error) {
      if (retryCount < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchWithAuth(url, options, retryCount + 1);
      }
      throw error;
    }
  };

  const contextValue = useMemo(() => ({
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    getAccessToken,
    fetchWithAuth,
  }), [isAuthenticated, isLoading, user, login, logout, getAccessToken, fetchWithAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};